/**
 * Profile Backup & Auto-Save Service
 * 
 * Features:
 * A) Auto-Sync to AWS
 * B) Persist after logout
 * C) Export/Import backups
 */

import authAPI from './auth';
import resumeAPI from './api';

const BACKUP_KEYS = {
  PROFILE: 'profile_backup_permanent',
  RESUMES: 'resumes_backup_permanent',
  LAST_SYNC: 'last_aws_sync',
  AUTO_SAVE: 'auto_save_enabled'
};

class ProfileBackupService {
  /**
   * Feature A: Auto-Sync to AWS
   * Automatically save profile changes to AWS DynamoDB
   */
  async syncToAWS(profileData, resumes = []) {
    try {
      console.log('[ProfileBackup] Syncing to AWS...');
      
      const user = authAPI.getCurrentUser();
      if (!user) {
        throw new Error('No user logged in');
      }

      // Save profile to AWS (via DynamoDB Users table)
      if (profileData) {
        await resumeAPI.updateUserProfile(profileData);
        console.log('[ProfileBackup] ✅ Profile synced to AWS');
      }

      // Save resumes to AWS
      for (const resume of resumes) {
        if (resume.resume_id) {
          await resumeAPI.updateResume(resume.resume_id, resume);
        } else {
          await resumeAPI.createResume(resume);
        }
      }
      console.log(`[ProfileBackup] ✅ ${resumes.length} resume(s) synced to AWS`);

      // Record sync time
      localStorage.setItem(BACKUP_KEYS.LAST_SYNC, new Date().toISOString());

      return { success: true, timestamp: new Date().toISOString() };
    } catch (error) {
      console.error('[ProfileBackup] Sync to AWS failed:', error);
      throw error;
    }
  }

  /**
   * Feature B: Persist After Logout
   * Save profile data that survives logout and browser closure
   */
  savePermanent(profileData, resumes = []) {
    try {
      console.log('[ProfileBackup] Saving permanent backup...');
      
      // Save profile
      if (profileData) {
        localStorage.setItem(BACKUP_KEYS.PROFILE, JSON.stringify(profileData));
      }

      // Save resumes
      if (resumes && resumes.length > 0) {
        localStorage.setItem(BACKUP_KEYS.RESUMES, JSON.stringify(resumes));
      }

      // Also save to multiple backup locations for redundancy
      const backupData = {
        profile: profileData,
        resumes: resumes,
        timestamp: new Date().toISOString(),
        version: '1.0'
      };

      localStorage.setItem('BACKUP_PERMANENT', JSON.stringify(backupData));
      localStorage.setItem('benjamin_madera_persistent_backup', JSON.stringify(backupData));

      console.log('[ProfileBackup] ✅ Permanent backup saved');
      return true;
    } catch (error) {
      console.error('[ProfileBackup] Failed to save permanent backup:', error);
      return false;
    }
  }

  /**
   * Restore permanent backup
   */
  restorePermanent() {
    try {
      // Try multiple backup locations
      const backupStr = 
        localStorage.getItem(BACKUP_KEYS.PROFILE) ||
        localStorage.getItem('BACKUP_PERMANENT') ||
        localStorage.getItem('benjamin_madera_persistent_backup');

      if (!backupStr) {
        console.log('[ProfileBackup] No permanent backup found');
        return null;
      }

      const backup = JSON.parse(backupStr);
      console.log('[ProfileBackup] ✅ Permanent backup restored');
      return backup;
    } catch (error) {
      console.error('[ProfileBackup] Failed to restore backup:', error);
      return null;
    }
  }

  /**
   * Feature C: Export Profile to File
   * Download profile as JSON file for safekeeping
   */
  exportToFile(profileData, resumes = []) {
    try {
      const exportData = {
        export_date: new Date().toISOString(),
        version: '1.0',
        profile: profileData,
        resumes: resumes,
        user: authAPI.getCurrentUser()
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `profile-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('[ProfileBackup] ✅ Profile exported to file');
      return true;
    } catch (error) {
      console.error('[ProfileBackup] Failed to export:', error);
      return false;
    }
  }

  /**
   * Import Profile from File
   */
  async importFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const importData = JSON.parse(e.target.result);
          console.log('[ProfileBackup] ✅ Profile imported from file');
          resolve(importData);
        } catch (error) {
          console.error('[ProfileBackup] Failed to parse import file:', error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }

  /**
   * Enable/Disable Auto-Save
   */
  setAutoSave(enabled) {
    localStorage.setItem(BACKUP_KEYS.AUTO_SAVE, enabled ? 'true' : 'false');
    console.log('[ProfileBackup] Auto-save:', enabled ? 'ENABLED' : 'DISABLED');
  }

  /**
   * Check if auto-save is enabled
   */
  isAutoSaveEnabled() {
    return localStorage.getItem(BACKUP_KEYS.AUTO_SAVE) === 'true';
  }

  /**
   * Get last sync timestamp
   */
  getLastSyncTime() {
    const timestamp = localStorage.getItem(BACKUP_KEYS.LAST_SYNC);
    return timestamp ? new Date(timestamp) : null;
  }

  /**
   * Auto-save on change (debounced)
   */
  scheduleAutoSave(profileData, resumes, delayMs = 2000) {
    if (!this.isAutoSaveEnabled()) {
      return;
    }

    // Clear existing timeout
    if (this._autoSaveTimeout) {
      clearTimeout(this._autoSaveTimeout);
    }

    // Schedule new save
    this._autoSaveTimeout = setTimeout(async () => {
      console.log('[ProfileBackup] Auto-saving...');
      
      // Save permanent backup (always)
      this.savePermanent(profileData, resumes);

      // Sync to AWS (if online)
      try {
        await this.syncToAWS(profileData, resumes);
      } catch (error) {
        console.warn('[ProfileBackup] Auto-sync to AWS failed, but local backup saved');
      }
    }, delayMs);
  }

  /**
   * Complete backup (all features combined)
   */
  async backupAll() {
    try {
      const user = authAPI.getCurrentUser();
      if (!user) {
        throw new Error('No user logged in');
      }

      // Get all user data
      const resumes = await resumeAPI.getResumes();
      const profileData = user;

      // A) Sync to AWS
      await this.syncToAWS(profileData, resumes);

      // B) Save permanent local backup
      this.savePermanent(profileData, resumes);

      // C) Export to file
      this.exportToFile(profileData, resumes);

      console.log('[ProfileBackup] ✅ Complete backup finished');
      return { success: true, timestamp: new Date().toISOString() };
    } catch (error) {
      console.error('[ProfileBackup] Complete backup failed:', error);
      throw error;
    }
  }
}

export const profileBackup = new ProfileBackupService();
export default profileBackup;
